import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CalendarIcon,
  Fuel,
  Save,
  Copy,
  History,
  Loader2,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { getFuelGradeColor } from '../utils/fuelGradeColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FuelPricesPage = () => {
  const { user, isAdmin, isSuperUser } = useAuth();
  const { stores } = useStore();
  
  // Only admins can access this page
  const canAccess = isAdmin || isSuperUser;
  
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [storeDetails, setStoreDetails] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentPrices, setCurrentPrices] = useState({});
  const [newPrices, setNewPrices] = useState({});
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentPriceDate, setCurrentPriceDate] = useState(null);
  const [existingPricesForDate, setExistingPricesForDate] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Filter fuel stations
  const fuelStations = useMemo(() => {
    return stores.filter(s => s.store_type === 'fuel_station');
  }, [stores]);
  
  // Auto-select first fuel station
  useEffect(() => {
    if (fuelStations.length > 0 && !selectedStoreId) {
      setSelectedStoreId(fuelStations[0].id);
    }
  }, [fuelStations, selectedStoreId]);
  
  // Fetch store details when store changes
  useEffect(() => {
    if (selectedStoreId) {
      fetchStoreDetails();
      fetchPriceHistory();
    }
  }, [selectedStoreId]);
  
  // Check for existing prices when date changes
  useEffect(() => {
    if (selectedStoreId && selectedDate) {
      checkExistingPricesForDate();
      fetchCurrentPrices();
    }
  }, [selectedStoreId, selectedDate]);
  
  const fetchStoreDetails = async () => {
    try {
      const response = await axios.get(`${API}/stores/${selectedStoreId}`);
      setStoreDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch store details:', error);
    }
  };
  
  const fetchCurrentPrices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fuel/retail-prices/current`, {
        params: { store_id: selectedStoreId, before_date: format(selectedDate, 'yyyy-MM-dd') }
      });
      
      if (response.data.has_prices) {
        setCurrentPrices(response.data.prices);
        setCurrentPriceDate(response.data.date);
      } else {
        setCurrentPrices({});
        setCurrentPriceDate(null);
      }
    } catch (error) {
      console.error('Failed to fetch current prices:', error);
      setCurrentPrices({});
    } finally {
      setLoading(false);
    }
  };
  
  const checkExistingPricesForDate = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/fuel/retail-prices`, {
        params: { store_id: selectedStoreId, price_date: dateStr, allow_fallback: false }
      });
      
      if (response.data.has_prices && response.data.source === 'exact_date') {
        setExistingPricesForDate(response.data.prices);
        setNewPrices(response.data.prices);
      } else {
        setExistingPricesForDate(null);
        setNewPrices({});
      }
    } catch (error) {
      setExistingPricesForDate(null);
      setNewPrices({});
    }
  };
  
  const fetchPriceHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/fuel/retail-prices/history`, {
        params: { store_id: selectedStoreId, limit: 30 }
      });
      setPriceHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch price history:', error);
      setPriceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const handleCopyCurrentToNew = () => {
    if (Object.keys(currentPrices).length === 0) {
      toast.error('No current prices to copy');
      return;
    }
    setNewPrices({...currentPrices});
    toast.success('Current prices copied to new prices');
  };
  
  const handleNewPriceChange = (gradeId, value) => {
    setNewPrices(prev => ({
      ...prev,
      [gradeId]: value
    }));
  };
  
  const handleSavePrices = async () => {
    // Only grades with a new price are saved; other grades keep their previous price
    const pricesToSave = Object.fromEntries(
      Object.entries(newPrices).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );
    if (Object.keys(pricesToSave).length === 0) {
      toast.error('Please enter a price for at least one fuel grade');
      return;
    }
    if (Object.values(pricesToSave).some(v => !(parseFloat(v) > 0))) {
      toast.error('Prices must be greater than zero');
      return;
    }
    
    setSaving(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await axios.post(`${API}/fuel/retail-prices`, {
        store_id: selectedStoreId,
        date: dateStr,
        prices: pricesToSave
      });
      
      toast.success(`Retail prices ${existingPricesForDate ? 'updated' : 'saved'} for ${format(selectedDate, 'dd MMM yyyy')}`);
      
      // Refresh data
      fetchPriceHistory();
      setExistingPricesForDate(pricesToSave);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save prices');
    } finally {
      setSaving(false);
    }
  };
  
  // Access denied for non-admins
  if (!canAccess) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-500/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-500">
              <AlertTriangle className="h-6 w-6" />
              <div>
                <h2 className="font-semibold">Access Denied</h2>
                <p className="text-sm text-muted-foreground">Only Administrators can access this page.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Fuel className="h-6 w-6 text-amber-500" />
            Fuel Retail Prices
          </h1>
          <p className="text-muted-foreground text-sm">Set daily retail prices for fuel grades (Admin Only)</p>
        </div>
      </div>
      
      {/* Store & Date Selection */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Store Selector */}
            <div className="space-y-1.5 min-w-[200px]">
              <Label>Fuel Station</Label>
              <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                <SelectTrigger data-testid="fuel-prices-store-select">
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  {fuelStations.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date Picker */}
            <div className="flex flex-col space-y-1.5">
              <Label>Price Date</Label>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px] justify-start text-left font-normal" data-testid="fuel-prices-date-picker">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(selectedDate, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => { if (date) { setSelectedDate(date); setDatePickerOpen(false); } }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {existingPricesForDate && (
              <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30" data-testid="prices-set-badge">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Prices set for this date
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Main Content */}
      <Tabs defaultValue="prices" className="w-full">
        <TabsList>
          <TabsTrigger value="prices" data-testid="set-prices-tab">Set Prices</TabsTrigger>
          <TabsTrigger value="history" data-testid="price-history-tab">
            <History className="h-4 w-4 mr-1" />
            Price History
          </TabsTrigger>
        </TabsList>
        
        {/* Price Entry Tab */}
        <TabsContent value="prices" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">
                    {existingPricesForDate ? 'Update' : 'Enter'} Prices for {format(selectedDate, 'dd MMM yyyy')}
                  </CardTitle>
                  <CardDescription>
                    {currentPriceDate 
                      ? `Current prices from ${format(new Date(currentPriceDate + 'T00:00:00'), 'dd MMM yyyy')}`
                      : 'No current prices set'
                    }
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCopyCurrentToNew}
                  data-testid="copy-current-prices-btn"
                  disabled={Object.keys(currentPrices).length === 0}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy Current to New
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : !storeDetails?.fuel_grades?.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Fuel className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No fuel grades configured for this store.</p>
                  <p className="text-sm">Please configure fuel grades in Store Settings.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Header Row */}
                  <div className="grid grid-cols-3 gap-4 text-sm font-medium text-muted-foreground border-b pb-2">
                    <div>Fuel Grade</div>
                    <div className="text-center">Current Price (ppl)</div>
                    <div className="text-center">New Price (ppl)</div>
                  </div>
                  
                  {/* Price Rows */}
                  {storeDetails.fuel_grades.map(grade => {
                    const gradeColors = getFuelGradeColor(grade.name);
                    const currentPrice = currentPrices[grade.id];
                    const newPrice = newPrices[grade.id] || '';
                    
                    return (
                      <div key={grade.id} className="grid grid-cols-3 gap-4 items-center">
                        <div className="flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-full ${gradeColors.bg}`}></span>
                          <span className="font-medium">{grade.name}</span>
                        </div>
                        <div className="text-center">
                          <span className="font-mono text-lg bg-muted/50 px-3 py-1 rounded" data-testid={`current-price-${grade.id}`}>
                            {currentPrice ? parseFloat(currentPrice).toFixed(1) : '-'}
                          </span>
                        </div>
                        <div className="flex justify-center">
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="e.g. 147.9"
                            value={newPrice}
                            onChange={(e) => handleNewPriceChange(grade.id, e.target.value)}
                            className="w-32 text-center font-mono text-lg"
                            data-testid={`new-price-${grade.id}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t">
                    <Button onClick={handleSavePrices} disabled={saving} data-testid="save-prices-btn">
                      {saving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          {existingPricesForDate ? 'Update Prices' : 'Save Prices'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Price History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="h-5 w-5" />
                Price History
              </CardTitle>
              <CardDescription>
                Recent price changes for {storeDetails?.name || 'selected store'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : priceHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No price history found.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {/* History Table Header */}
                  <div className="grid grid-cols-4 gap-2 text-xs font-medium text-muted-foreground border-b pb-2 sticky top-0 bg-background">
                    <div>Date</div>
                    <div>Prices (ppl)</div>
                    <div>Set By</div>
                    <div>Changed At</div>
                  </div>
                  
                  {priceHistory.map((entry, idx) => (
                    <div key={idx} data-testid={`price-history-row-${idx}`} className="grid grid-cols-4 gap-2 items-center text-sm py-2 border-b border-border/50">
                      <div className="font-medium">
                        {format(new Date(entry.date + 'T00:00:00'), 'dd MMM yyyy')}
                      </div>
                      <div className="text-xs font-mono">
                        {Object.entries(entry.prices || {}).map(([gradeId, price], i) => {
                          const grade = storeDetails?.fuel_grades?.find(g => g.id === gradeId);
                          const gradeName = grade?.name?.substring(0, 3).toUpperCase() || gradeId;
                          return (
                            <span key={gradeId}>
                              {i > 0 && ' | '}
                              {gradeName}: {parseFloat(price).toFixed(1)}
                            </span>
                          );
                        })}
                      </div>
                      <div className="text-muted-foreground">
                        {entry.changed_by_name || 'Admin'}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {entry.changed_at && format(new Date(entry.changed_at), 'dd/MM HH:mm')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FuelPricesPage;
